import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Input,
  Spin,
  Empty,
  message,
  Select,
  Space,
  Modal,
  Button,
  Row,
  Col,
} from 'antd';
import { SearchOutlined, PictureOutlined } from '@ant-design/icons';
import { pexelsApi } from '../../../../api';
import Masonry from 'react-masonry-css';
import './PexelsImageSearchImproved.css';

const { Search } = Input;
const { Option } = Select;

/**
 * Pexels画像検索コンポーネント（改善版）
 * - エンドレススクロール
 * - 正方形カード + 実際の縦横比
 * - vh90で追従表示
 */
const PexelsImageSearchImproved = ({ articleKeyword, onImageSelect }) => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [photos, setPhotos] = useState([]);
  const [orientation, setOrientation] = useState('landscape');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [perPage] = useState(20);
  const scrollContainerRef = useRef(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  // 初回読み込み: キュレーション画像を表示
  const loadCuratedPhotos = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const data = await pexelsApi.getCuratedPhotos(perPage, page);
        if (page === 1) {
          setPhotos(data.photos || []);
        } else {
          setPhotos(prev => [...prev, ...(data.photos || [])]);
        }
        setHasMore((data.photos || []).length === perPage);
        setCurrentPage(page);
      } catch (error) {
        console.error('キュレーション画像の取得に失敗:', error);
        message.error('画像の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    },
    [perPage],
  );

  useEffect(() => {
    loadCuratedPhotos(1);
  }, [loadCuratedPhotos]);

  // 画像を検索
  const handleSearch = async (query = searchQuery, page = 1) => {
    if (!query.trim()) {
      message.warning('検索キーワードを入力してください');
      return;
    }

    try {
      setLoading(true);
      const data = await pexelsApi.searchPhotos(
        query,
        orientation,
        perPage,
        page,
      );

      if (page === 1) {
        setPhotos(data.photos || []);
      } else {
        setPhotos(prev => [...prev, ...(data.photos || [])]);
      }

      setHasMore((data.photos || []).length === perPage);
      setCurrentPage(page);
    } catch (error) {
      console.error('画像検索に失敗:', error);
      message.error('画像検索に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // スクロール検知（エンドレススクロール）
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;

    // 下から200pxの位置で次のページを読み込む
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      const nextPage = currentPage + 1;
      if (searchQuery.trim()) {
        handleSearch(searchQuery, nextPage);
      } else {
        loadCuratedPhotos(nextPage);
      }
    }
  }, [loading, hasMore, currentPage, searchQuery, loadCuratedPhotos]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // 画像の向きを変更
  const handleOrientationChange = value => {
    setOrientation(value);
    if (searchQuery.trim()) {
      setCurrentPage(1);
      handleSearch(searchQuery, 1);
    }
  };

  // 画像クリック（プレビュー）
  const handleImageClick = photo => {
    setPreviewPhoto(photo);
  };

  // ドラッグ開始
  const handleDragStart = (e, photo) => {
    const imageData = {
      type: 'image',
      url: photo.src.large,
      photographer: photo.photographer,
      photographer_url: photo.photographer_url,
      alt: photo.alt || photo.photographer,
    };
    e.dataTransfer.setData('image', JSON.stringify(imageData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div>
      {/* 検索エリア */}
      <Space
        direction="vertical"
        style={{ width: '100%', marginBottom: 16 }}
        size="small"
      >
        <Row gutter={8} align="middle">
          {/* 向きセレクトを先に */}
          <Col span={7}>
            <Select
              value={orientation}
              onChange={handleOrientationChange}
              style={{ width: '100%' }}
              size="middle"
            >
              <Option value="landscape">横向き</Option>
              <Option value="portrait">縦向き</Option>
              <Option value="square">正方形</Option>
            </Select>
          </Col>
          {/* 検索ボックスを後に */}
          <Col span={17}>
            <Search
              placeholder={
                articleKeyword ? `"${articleKeyword}" で検索` : '画像を検索...'
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onSearch={() => {
                setCurrentPage(1);
                handleSearch(searchQuery, 1);
              }}
              enterButton={<SearchOutlined />}
              size="middle"
            />
          </Col>
        </Row>
      </Space>

      {/* 画像グリッド（エンドレススクロール + Masonry） */}
      <div
        ref={scrollContainerRef}
        className="pexels-scroll-container"
        style={{
          maxHeight: 'calc(100vh - 300px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {photos.length === 0 && !loading ? (
          <Empty description="画像が見つかりませんでした" />
        ) : (
          <Masonry
            breakpointCols={2}
            className="pexels-masonry-grid"
            columnClassName="pexels-masonry-grid_column"
          >
            {photos.map(photo => (
              <div
                key={photo.id}
                className="pexels-image-card"
                onClick={() => handleImageClick(photo)}
                onDragStart={e => handleDragStart(e, photo)}
                draggable
                style={{
                  marginBottom: 12,
                  borderRadius: 8,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                <img
                  src={photo.src.medium}
                  alt={photo.alt || photo.photographer}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
                <div
                  className="pexels-image-overlay"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    color: 'white',
                    padding: '30px 8px 8px',
                    fontSize: 11,
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    pointerEvents: 'none',
                  }}
                >
                  {photo.photographer}
                </div>
              </div>
            ))}
          </Masonry>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin />
          </div>
        )}
      </div>

      {/* 使用上の注意 */}
      <div
        style={{
          marginTop: 12,
          padding: 12,
          background: '#f0f9ff',
          borderRadius: 8,
          fontSize: 11,
          color: '#1890ff',
        }}
      >
        <strong>Pexels:</strong> すべて著作権フリー、商用利用可能（クリック:
        プレビュー / ドラッグ: 挿入）
      </div>

      {/* プレビューモーダル */}
      <Modal
        open={!!previewPhoto}
        onCancel={() => setPreviewPhoto(null)}
        footer={null}
        width="80%"
        centered
      >
        {previewPhoto && (
          <div style={{ textAlign: 'center' }}>
            <img
              src={previewPhoto.src.large}
              alt={previewPhoto.alt || previewPhoto.photographer}
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
              draggable
              onDragStart={e => handleDragStart(e, previewPhoto)}
            />
            <div style={{ marginTop: 16, color: '#666' }}>
              Photo by{' '}
              <a
                href={previewPhoto.photographer_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {previewPhoto.photographer}
              </a>{' '}
              on Pexels
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              ドラッグ＆ドロップでエディタに挿入できます
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PexelsImageSearchImproved;
